import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, Volume1 } from "lucide-react";

/**
 * Ambient Mediterranean music with volume slider.
 * - Tries to autoplay on mount (browsers often block until first user gesture).
 * - On first user interaction anywhere on the page, audio starts.
 * - Floating gold control bottom-right lets the user toggle mute and adjust volume.
 */
export const BackgroundMusic = ({ src = "/mediterranean.mp3", initialVolume = 0.35 }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(initialVolume);
  const [open, setOpen] = useState(false);

  // Try to autoplay; if blocked, attach one-shot listeners for first gesture.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = volume;
    a.loop = true;

    const tryPlay = () => {
      a.play()
        .then(() => setPlaying(true))
        .catch(() => {
          const start = () => {
            a.play().then(() => setPlaying(true)).catch(() => {});
            window.removeEventListener("click", start);
            window.removeEventListener("touchstart", start);
            window.removeEventListener("keydown", start);
            window.removeEventListener("scroll", start);
          };
          window.addEventListener("click", start, { once: true });
          window.addEventListener("touchstart", start, { once: true });
          window.addEventListener("keydown", start, { once: true });
          window.addEventListener("scroll", start, { once: true });
        });
    };
    tryPlay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync volume into the audio element when slider changes.
  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = volume;
    if (volume > 0 && a.muted) {
      a.muted = false;
      setMuted(false);
    }
  }, [volume]);

  const toggleMute = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play().then(() => {
        setPlaying(true);
        a.muted = false;
        setMuted(false);
      }).catch(() => {});
      return;
    }
    a.muted = !a.muted;
    setMuted(a.muted);
  };

  const isQuiet = !playing || muted || volume === 0;
  const Icon = isQuiet ? VolumeX : volume < 0.5 ? Volume1 : Volume2;

  return (
    <>
      <audio
        ref={audioRef}
        src={src}
        preload="auto"
        loop
        data-testid="bg-music-audio"
      />

      {/* Floating control cluster bottom-right */}
      <div
        className="fixed bottom-6 right-6 z-[120] flex items-center gap-3"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        {/* Expanding volume slider */}
        <div
          data-testid="bg-music-slider-wrap"
          className="overflow-hidden transition-all duration-300 ease-out"
          style={{
            width: open ? 140 : 0,
            opacity: open ? 1 : 0,
          }}
        >
          <div className="flex items-center h-12 px-4 rounded-full border border-[color:var(--goldenelia-gold)]/60 bg-black/85 backdrop-blur-md">
            <input
              data-testid="bg-music-volume"
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              aria-label="Volume"
              className="goldenelia-volume w-full"
              style={{ accentColor: "var(--goldenelia-gold)" }}
            />
          </div>
        </div>

        <button
          data-testid="bg-music-toggle"
          onClick={toggleMute}
          onFocus={() => setOpen(true)}
          aria-label={isQuiet ? "Unmute" : "Mute"}
          className="w-12 h-12 rounded-full flex items-center justify-center border border-[color:var(--goldenelia-gold)]/60 bg-black/85 backdrop-blur-md text-[color:var(--goldenelia-gold)] hover:bg-[color:var(--goldenelia-gold)] hover:text-black transition-colors shadow-[0_0_30px_rgba(201,169,97,0.18)]"
          style={{
            animation: isQuiet ? "goldeneliaMusicPulse 2.2s ease-in-out infinite" : "none",
          }}
        >
          <Icon size={18} />
        </button>
      </div>

      <style>{`
        @keyframes goldeneliaMusicPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(201,169,97,0.55), 0 0 30px rgba(201,169,97,0.18); }
          50% { box-shadow: 0 0 0 12px rgba(201,169,97,0), 0 0 30px rgba(201,169,97,0.18); }
        }
        input.goldenelia-volume {
          appearance: none;
          -webkit-appearance: none;
          height: 2px;
          background: linear-gradient(to right, var(--goldenelia-gold) 0%, var(--goldenelia-gold) ${volume * 100}%, rgba(201,169,97,0.25) ${volume * 100}%, rgba(201,169,97,0.25) 100%);
          outline: none;
          border-radius: 2px;
        }
        input.goldenelia-volume::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--goldenelia-gold-bright, var(--goldenelia-gold));
          cursor: pointer;
          box-shadow: 0 0 8px rgba(201,169,97,0.55);
          border: none;
        }
        input.goldenelia-volume::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: var(--goldenelia-gold-bright, var(--goldenelia-gold));
          cursor: pointer;
          box-shadow: 0 0 8px rgba(201,169,97,0.55);
          border: none;
        }
      `}</style>
    </>
  );
};

export default BackgroundMusic;
